/**
 * AudioSettingsTab
 *
 * WebRTC audio device selection and test panel.
 * - Enumerates and selects microphone and speaker
 * - Persists selection via webrtcAudioDeviceStore
 * - Test microphone: level meter from live stream
 * - Test speaker: beep routed through selected output (when setSinkId supported)
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import MicIcon from '@mui/icons-material/Mic';
import RefreshIcon from '@mui/icons-material/Refresh';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAudioDevices, useSipCallStore, useWebrtcAudioDeviceStore } from '../application';

// ============================================================================
// Helpers
// ============================================================================

const supportsSetSinkId = typeof document !== 'undefined' && typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

// ============================================================================
// Component
// ============================================================================

export function AudioSettingsTab() {
  const { inputs, outputs, isLoading, error, refresh, requestPermission } = useAudioDevices();
  const audioInputDeviceId = useWebrtcAudioDeviceStore(s => s.audioInputDeviceId);
  const audioOutputDeviceId = useWebrtcAudioDeviceStore(s => s.audioOutputDeviceId);
  const setAudioInputDeviceId = useWebrtcAudioDeviceStore(s => s.setAudioInputDeviceId);
  const setAudioOutputDeviceId = useWebrtcAudioDeviceStore(s => s.setAudioOutputDeviceId);
  const activeSession = useSipCallStore(s => s.activeSession);
  const replaceInputTrack = useSipCallStore(s => s.replaceInputTrack);

  const [permissionRequesting, setPermissionRequesting] = useState(false);
  const [inputTestActive, setInputTestActive] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const [outputTestPlaying, setOutputTestPlaying] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const handleRequestPermission = useCallback(async () => {
    setPermissionRequesting(true);
    try {
      await requestPermission();
    } finally {
      setPermissionRequesting(false);
    }
  }, [requestPermission]);

  const stopInputTest = useCallback(() => {
    if (animationRef.current != null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setInputTestActive(false);
    setInputLevel(0);
  }, []);

  const startInputTest = useCallback(async () => {
    // Guard for environments where mediaDevices/getUserMedia may be absent (e.g. non-secure context, old browsers)
    const mediaDevices = typeof navigator !== 'undefined' ? (navigator as Navigator & { mediaDevices?: { getUserMedia?: unknown } }).mediaDevices : undefined;
    if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') return;
    stopInputTest();
    try {
      const constraints: MediaStreamConstraints = {
        audio: audioInputDeviceId ? { deviceId: { exact: audioInputDeviceId } } : true,
        video: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = analyser;
      setInputTestActive(true);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const tick = () => {
        const anal = analyserRef.current;
        if (!anal) return;
        anal.getByteFrequencyData(dataArray);
        const sum = dataArray.reduce((a, b) => a + b, 0);
        const avg = sum / dataArray.length;
        setInputLevel(Math.min(100, (avg / 255) * 150));
        animationRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.warn('[WebRTC] Input test failed', err);
      stopInputTest();
    }
  }, [audioInputDeviceId, stopInputTest]);

  const playOutputTest = useCallback(async () => {
    if (outputTestPlaying) return;
    try {
      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 440;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(dest);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);

      const audio = new Audio();
      audio.srcObject = dest.stream;
      setOutputTestPlaying(true);
      if (supportsSetSinkId && audioOutputDeviceId) {
        try {
          await (audio as HTMLAudioElement & { setSinkId(id: string): Promise<void> }).setSinkId(audioOutputDeviceId);
        } catch {
          // fallback to default output
        }
      }
      await audio.play();
      setTimeout(() => {
        audio.srcObject = null;
        setOutputTestPlaying(false);
      }, 400);
    } catch (err) {
      console.warn('[WebRTC] Output test failed', err);
      setOutputTestPlaying(false);
    }
  }, [audioOutputDeviceId, outputTestPlaying]);

  useEffect(() => {
    return () => stopInputTest();
  }, [stopInputTest]);

  const handleInputChange = (deviceId: string) => {
    setAudioInputDeviceId(deviceId || '');
    if (activeSession) {
      void replaceInputTrack();
    }
  };

  return (
    <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => void refresh()} disabled={isLoading}>
          Refresh devices
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => void handleRequestPermission()}
          disabled={permissionRequesting || !('mediaDevices' in navigator && typeof navigator.mediaDevices.getUserMedia === 'function')}
        >
          {permissionRequesting ? 'Requesting…' : 'Grant microphone permission'}
        </Button>
      </Box>

      <FormControl fullWidth size="small">
        <InputLabel id="audio-input-label">Microphone</InputLabel>
        <Select
          labelId="audio-input-label"
          label="Microphone"
          value={audioInputDeviceId || ''}
          onChange={e => handleInputChange(e.target.value || '')}
          disabled={isLoading}
        >
          <MenuItem value="">Default</MenuItem>
          {inputs.map(dev => (
            <MenuItem key={dev.deviceId} value={dev.deviceId}>
              {dev.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Typography variant="body2" sx={{ mb: 1 }} fontWeight={500}>
          Test microphone
        </Typography>
        {inputTestActive ? (
          <Box>
            <LinearProgress variant="determinate" value={inputLevel} sx={{ mb: 1, height: 8, borderRadius: 1 }} />
            <Button size="small" variant="outlined" color="error" onClick={stopInputTest}>
              Stop test
            </Button>
          </Box>
        ) : (
          <Button size="small" variant="outlined" startIcon={<MicIcon />} onClick={() => void startInputTest()} disabled={isLoading}>
            Start microphone test
          </Button>
        )}
      </Box>

      <FormControl fullWidth size="small">
        <InputLabel id="audio-output-label">Speaker</InputLabel>
        <Select labelId="audio-output-label" label="Speaker" value={audioOutputDeviceId || ''} onChange={e => setAudioOutputDeviceId(e.target.value || '')} disabled={isLoading || !supportsSetSinkId}>
          <MenuItem value="">Default</MenuItem>
          {outputs.map(dev => (
            <MenuItem key={dev.deviceId} value={dev.deviceId}>
              {dev.label}
            </MenuItem>
          ))}
        </Select>
        {!supportsSetSinkId && <FormHelperText>Output device selection is not supported in this browser.</FormHelperText>}
      </FormControl>

      <Box>
        <Typography variant="body2" sx={{ mb: 1 }} fontWeight={500}>
          Test speaker
        </Typography>
        <Button size="small" variant="outlined" startIcon={<VolumeUpIcon />} onClick={() => void playOutputTest()} disabled={outputTestPlaying}>
          {outputTestPlaying ? 'Playing…' : 'Play test tone'}
        </Button>
        {!supportsSetSinkId && <FormHelperText sx={{ mt: 0.5 }}>Output device selection not supported; tone uses default speaker.</FormHelperText>}
      </Box>
    </Box>
  );
}
